import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { requestPasswordReset } from '../../lib/api/auth';
import { GENERIC_NETWORK_ERROR, normalizeError } from '../../lib/errors';
import { Button, Notice } from '../../components/primitives';
import { TextField } from '../../components/form';
import { AuthShell } from './AuthShell';

const schema = z.object({
  email: z.string().min(1, 'Ingrese su correo institucional.').email('El correo no tiene un formato válido.'),
});

type ForgotForm = z.infer<typeof schema>;

/** Mensaje idéntico exista o no la cuenta: el formulario no enumera usuarios. */
const GENERIC_CONFIRMATION = 'Si el correo existe, recibirá instrucciones.';

export function ForgotPasswordPage(): JSX.Element {
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotForm>({ resolver: zodResolver(schema), defaultValues: { email: '' } });

  const onSubmit = async (values: ForgotForm): Promise<void> => {
    setFormError(null);
    try {
      await requestPasswordReset(values.email.trim());
    } catch (error) {
      const normalized = normalizeError(error);
      // Sólo un fallo de red o de tasa se informa: cualquier otro resultado se
      // confirma igual, para no revelar si la cuenta existe.
      if (normalized.code === 'NETWORK') {
        setFormError(GENERIC_NETWORK_ERROR);
        return;
      }
      if (normalized.statusCode === 429) {
        setFormError('Demasiadas solicitudes. Espere unos minutos antes de volver a intentarlo.');
        return;
      }
    }
    setSent(true);
  };

  return (
    <AuthShell
      title="Recuperar contraseña"
      subtitle="Le enviaremos un enlace de restablecimiento al correo institucional asociado a su cuenta."
    >
      {sent ? (
        <div className="stack">
          <Notice tone="info" role="status">
            {GENERIC_CONFIRMATION} Revise también la carpeta de correo no deseado. El enlace tiene una
            vigencia limitada.
          </Notice>
          <Link className="btn btn--block" to="/login">
            Volver al inicio de sesión
          </Link>
        </div>
      ) : (
        <form className="auth__form" onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
          {formError !== null && (
            <Notice tone="error" role="alert">
              {formError}
            </Notice>
          )}

          <TextField
            label="Correo institucional"
            type="email"
            autoComplete="username"
            autoFocus
            required
            placeholder="nombre.apellido@oxxean.cl"
            error={errors.email?.message}
            {...register('email')}
          />

          <Button type="submit" variant="primary" block loading={isSubmitting}>
            {isSubmitting ? 'Enviando…' : 'Enviar instrucciones'}
          </Button>

          <Link className="tiny" to="/login">
            Volver al inicio de sesión
          </Link>
        </form>
      )}
    </AuthShell>
  );
}
